import { Component, inject, InjectionToken } from '@angular/core';
import { ModalRef } from '@fmnts/components/modal';
import { I18nModule } from '@fmnts/i18n';
import { SelfCheckoutErrorModalUiComponent } from './self-checkout-modal-ui.component';

interface SelfCheckoutOptions {
  clientOfflineErrorTitle: string;
  clientOfflineErrorDescription: string;
  networkErrorCookiesDisabledTitle: string;
  networkErrorCookiesDisabledDescription: string;
  clientTimeOutErrorTitle: string;
  clientTimeOutErrorDescription: string;
}

/**
 * DI token to access configuration for icons related to activities.
 */
const SelfCheckoutOptionsToken = new InjectionToken<SelfCheckoutOptions>(
  'selfcheckout.ui.options',
  {
    providedIn: 'root',
    factory: () => ({
      clientTimeOutErrorDescription: 'client_timeout_error_description',
      clientOfflineErrorTitle: 'client_offline_error_title',
      clientOfflineErrorDescription: 'client_offline_error_description',
      networkErrorCookiesDisabledTitle: 'network_error_cookies_disabled_title',
      networkErrorCookiesDisabledDescription:
        'network_error_cookies_disabled_description',
      clientTimeOutErrorTitle: 'client_timeout_error_title',
    }),
  },
);
/**
 * Custom error modal to show when CSRF verification failed
 *
 * This can happen when all Cookies are disabled on the device
 */
@Component({
  selector: 'verify-cookies-disabled-modal',
  template: `<verify-self-checkout-error-modal
    [title]="tokens.networkErrorCookiesDisabledTitle | translate"
    [content]="tokens.networkErrorCookiesDisabledDescription | translate"
    (onClose)="close($event)"
  ></verify-self-checkout-error-modal>`,
  standalone: true,
  imports: [I18nModule, SelfCheckoutErrorModalUiComponent],
})
export class CookiesDisabledModalComponent {
  readonly tokens = inject(SelfCheckoutOptionsToken);
  private readonly _modalRef = inject(ModalRef<CookiesDisabledModalComponent>);

  public close(result: string): void {
    this._modalRef.close(result);
  }
}

/**
 * Custom error modal to show when the client is offline
 *
 * This can happen when the device doesn't have internet connection
 */
@Component({
  selector: 'verify-client-offline-modal',
  template: `<verify-self-checkout-error-modal
    [title]="tokens.clientOfflineErrorTitle | translate"
    [content]="tokens.clientOfflineErrorDescription | translate"
    (onClose)="close($event)"
  ></verify-self-checkout-error-modal>`,
  standalone: true,
  imports: [I18nModule, SelfCheckoutErrorModalUiComponent],
})
export class ClientOfflineModalComponent {
  readonly tokens = inject(SelfCheckoutOptionsToken);
  private readonly _modalRef = inject(ModalRef<ClientOfflineModalComponent>);

  public close(result: string): void {
    this._modalRef.close(result);
  }
}

/**
 * Custom error modal to show when the client gets a TimeoutError
 *
 * This can happen when the connection to the server takes more than expected
 */
@Component({
  selector: 'verify-client-timeout-modal',
  template: `<verify-self-checkout-error-modal
    [title]="tokens.clientTimeOutErrorTitle | translate"
    [content]="tokens.clientTimeOutErrorDescription | translate"
    (onClose)="close($event)"
  ></verify-self-checkout-error-modal>`,
  standalone: true,
  imports: [I18nModule, SelfCheckoutErrorModalUiComponent],
})
export class ClientTimeoutModalComponent {
  readonly tokens = inject(SelfCheckoutOptionsToken);
  private readonly _modalRef = inject(ModalRef<ClientTimeoutModalComponent>);

  public close(result: string): void {
    this._modalRef.close(result);
  }
}
